/* One dashboard chart, fetched and drawn — or explaining why it isn't.
 *
 * Kept apart from `Chart` so the drawing stays a pure function of its data:
 * this is the part that talks to the gateway, and the part that has to
 * survive a chart failing. ERPNext's Report-backed charts run a whole report
 * to draw themselves and some of them raise on a clean site, so a tile that
 * cannot be drawn says so and its neighbours carry on.
 */
import { useEffect, useState } from 'react'
import { Chart } from './Chart'
import { useErp } from './context'

export function ChartTile({ name, title, type, height }: {
  name: string; title?: string; type?: string | null
  /** Taller where there is room: four charts across at 320px was a row of
   *  scribbles with colliding axis labels. */
  height?: number
}) {
  const erp = useErp()
  const [state, setState] = useState<{ loading: boolean; labels: string[]; datasets: { name?: string; values?: (number | null)[] }[]; why?: string }>(
    { loading: true, labels: [], datasets: [] })

  useEffect(() => {
    const controller = new AbortController()
    setState({ loading: true, labels: [], datasets: [] })
    erp.chartData(name, controller.signal)
      .then((r) => setState({
        loading: false,
        labels: r.labels ?? [],
        datasets: r.datasets ?? [],
        why: r.ok ? undefined : (r.why ?? 'This chart could not be drawn.'),
      }))
      .catch((e) => {
        if ((e as Error).name === 'AbortError') return
        setState({ loading: false, labels: [], datasets: [], why: 'This chart could not be loaded.' })
      })
    return () => controller.abort()
  }, [erp, name])

  if (state.loading) {
    return (
      <figure className="card card-pad" style={{ margin: 0 }}>
        <figcaption className="small" style={{ fontWeight: 600, marginBottom: 8 }}>{title ?? name}</figcaption>
        <div className="skel" style={{ height: height ?? 150 }} />
      </figure>
    )
  }
  return <Chart title={title ?? name} labels={state.labels} datasets={state.datasets}
                type={type} why={state.why} height={height} />
}
